// Renders the synthesis of a multi-PDP audit report (report_type category |
// brand | comparison): executive summary, ranked PDPs, common gaps,
// in-network placement recommendations, next steps — plus an expandable
// per-PDP section that reuses the full single-audit renderer
// (PdpAuditReport).
//
// Visual language matches PdpAuditReport: white cards, gold accents
// (CSS-var aware so the RMN white-label palette applies), Oswald display +
// Lato body.

import React, { useState } from "react";
import { PdpAuditReport, toCtaProductRef } from "./PdpAuditReport";
import { PdpAudit, PdpAuditResponse } from "./types";
import {
    ReportProductRef,
    RmnAuditSynthesis,
    RmnBrandReportParams,
    RmnCategoryReportParams,
    RmnComparisonReportParams,
    RmnMultiAuditItem,
    RmnMultiAuditReport
} from "../../types/rmn";

const GOLD = "var(--rmn-primary, #b8966a)";
const GOLD_TINT = "var(--rmn-primary-tint, #FAF5ED)";
// Neutral roles: literal = light/public value; --tool-* exists only in the
// portal's dark mode (src/theme/tokens.ts). See PdpAuditReport.tsx.
const BLACK = "var(--tool-text, #1a1a1a)";
const WHITE = "#ffffff";
const CARD = "var(--tool-card, #ffffff)";
const PAGE = "var(--tool-page, #ffffff)";
const GRAY = "var(--tool-text-2, #666666)";
const GRAY_LIGHT = "var(--tool-muted, #999999)";
const BORDER = "var(--tool-border, #e5e5e5)";
const GREEN = "var(--tool-up, #5A9E6F)";
const ERROR = "var(--tool-down, #b00020)";

const DISPLAY = "'Oswald', sans-serif";
const BODY = "'Lato', sans-serif";

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
    children,
    style
}) => (
    <div
        style={{
            background: CARD,
            borderRadius: 8,
            border: `1px solid ${BORDER}`,
            padding: "28px 32px",
            marginBottom: 20,
            ...style
        }}
    >
        {children}
    </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2
        style={{
            fontFamily: DISPLAY,
            fontSize: 20,
            fontWeight: 600,
            color: BLACK,
            margin: "0 0 16px"
        }}
    >
        {children}
    </h2>
);

const BulletList: React.FC<{ items: string[]; ordered?: boolean }> = ({ items, ordered }) => {
    const ListTag = ordered ? "ol" : "ul";
    return (
        <ListTag style={{ margin: 0, paddingLeft: 20 }}>
            {items.map((text, i) => (
                <li
                    key={i}
                    style={{
                        fontFamily: BODY,
                        fontSize: 13.5,
                        color: GRAY,
                        lineHeight: 1.65,
                        marginBottom: 8
                    }}
                >
                    {text}
                </li>
            ))}
        </ListTag>
    );
};

// Rebuild the PdpAuditResponse shape the single-audit renderer expects from a
// stored multi-audit item (same trick as PastAudits' rowToResponse). Exported
// for the zip builder in reportFiles.tsx, which feeds each item to PdpAuditPdf.
// reportId: the parent report's id — the per-page Create Strategy CTA sends
// it (with the page url) so the wizard's picker can read this page's audit.
export const itemToResponse = (item: RmnMultiAuditItem<PdpAudit>, reportId?: number | null): PdpAuditResponse => ({
    audit: item.audit as PdpAudit,
    meta: {
        url: item.url,
        finalUrl: item.url,
        title: "",
        metaDescription: "",
        jsonLdBlockCount: 0,
        geminiStatus: "success",
        geminiStatusCode: null,
        geminiError: null,
        reportId: reportId || null
    }
});

interface Props {
    reportType: string; // 'category' | 'brand' | 'comparison'
    report: RmnMultiAuditReport<PdpAudit>;
    params?: RmnCategoryReportParams | RmnBrandReportParams | RmnComparisonReportParams | null;
    bgColor?: string;
    // Passed through to the per-item audit views' Create Proposal CTA (the
    // brand portal points it at its own wizard and relabels it "Create
    // Strategy").
    proposalPath?: string;
    proposalCtaLabel?: string;
    // Per-URL product identity for the nested audits' CTAs (TDC-1499) —
    // keyed by item.url, forwarded so the wizard opens with the product
    // linked.
    productRefs?: Record<string, ReportProductRef>;
    // The report row's id, for the per-page CTAs (see itemToResponse).
    reportId?: number | null;
}

export function MultiAuditSynthesisReport({
    reportType,
    report,
    params,
    bgColor,
    proposalPath,
    reportId,
    proposalCtaLabel,
    productRefs
}: Props) {
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const synthesis: RmnAuditSynthesis | undefined = report?.synthesis;
    const items: RmnMultiAuditItem<PdpAudit>[] = report?.items || [];

    const isCategory = reportType === "category";
    const isBrand = reportType === "brand";
    let overline = "PDP Comparison";
    let rollupName: string | undefined;
    if (isCategory) {
        overline = "Category Report";
        rollupName = (params as RmnCategoryReportParams | null | undefined)?.category_name;
    } else if (isBrand) {
        overline = "Brand Report";
        rollupName = (params as RmnBrandReportParams | null | undefined)?.brand_name;
    }
    const auditedCount = items.filter((i) => i.audit).length;
    const subtitle = isCategory || isBrand
        ? [rollupName, `${auditedCount} product pages audited`].filter(Boolean).join(" · ")
        : `${auditedCount} product pages compared`;

    const toggle = (idx: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            return next;
        });

    return (
        <div style={{ fontFamily: BODY, background: bgColor || PAGE, minHeight: "100%" }}>
            <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 48px" }}>
                {/* ── Header ── */}
                <Card>
                    <div
                        style={{
                            fontFamily: BODY,
                            fontSize: 11,
                            letterSpacing: 1,
                            color: GRAY_LIGHT,
                            textTransform: "uppercase",
                            marginBottom: 4
                        }}
                    >
                        {overline}
                    </div>
                    <h1
                        style={{
                            fontFamily: DISPLAY,
                            fontSize: 26,
                            fontWeight: 700,
                            color: BLACK,
                            margin: "0 0 4px"
                        }}
                    >
                        {synthesis?.title || overline}
                    </h1>
                    <div style={{ fontFamily: BODY, fontSize: 13, color: GRAY_LIGHT, marginBottom: 16 }}>
                        {subtitle}
                    </div>
                    {synthesis?.summary && (
                        <p
                            style={{
                                fontFamily: BODY,
                                fontSize: 14.5,
                                color: GRAY,
                                lineHeight: 1.7,
                                margin: 0
                            }}
                        >
                            {synthesis.summary}
                        </p>
                    )}
                    {!synthesis && (
                        <p style={{ fontFamily: BODY, fontSize: 14, color: ERROR, margin: 0 }}>
                            The rollup synthesis is unavailable for this report; the individual page
                            audits are listed below.
                        </p>
                    )}
                </Card>

                {/* ── Rankings ── */}
                {synthesis && synthesis.rankings.length > 0 && (
                    <Card>
                        <SectionTitle>Rankings</SectionTitle>
                        {synthesis.rankings.map((r) => (
                            <div
                                key={`${r.rank}-${r.url}`}
                                style={{
                                    display: "flex",
                                    gap: 16,
                                    padding: "16px 0",
                                    borderTop: `1px solid ${BORDER}`
                                }}
                            >
                                <div
                                    style={{
                                        width: 34,
                                        height: 34,
                                        flexShrink: 0,
                                        borderRadius: "50%",
                                        background: r.rank === 1 ? GOLD : GOLD_TINT,
                                        color: r.rank === 1 ? WHITE : BLACK,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontFamily: DISPLAY,
                                        fontSize: 15,
                                        fontWeight: 600
                                    }}
                                >
                                    {r.rank}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            gap: 12,
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontFamily: DISPLAY,
                                                fontSize: 16,
                                                fontWeight: 600,
                                                color: BLACK
                                            }}
                                        >
                                            {r.productName || "Unknown product"}
                                        </span>
                                        <span
                                            style={{
                                                fontFamily: DISPLAY,
                                                fontSize: 16,
                                                fontWeight: 500,
                                                color: GOLD,
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {r.overall}%
                                            <span
                                                style={{
                                                    fontFamily: BODY,
                                                    fontSize: 10,
                                                    color: GRAY_LIGHT,
                                                    marginLeft: 6,
                                                    letterSpacing: 1,
                                                    textTransform: "uppercase"
                                                }}
                                            >
                                                Overall
                                            </span>
                                        </span>
                                    </div>
                                    <a
                                        href={r.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={r.url}
                                        style={{
                                            fontFamily: BODY,
                                            fontSize: 12,
                                            color: GRAY_LIGHT,
                                            display: "block",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            margin: "2px 0 10px"
                                        }}
                                    >
                                        {r.url}
                                    </a>
                                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                                        {r.highlights.length > 0 && (
                                            <div style={{ flex: "1 1 240px", minWidth: 220 }}>
                                                <div
                                                    style={{
                                                        fontFamily: BODY,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: GREEN,
                                                        textTransform: "uppercase",
                                                        letterSpacing: 0.5,
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    Highlights
                                                </div>
                                                <BulletList items={r.highlights} />
                                            </div>
                                        )}
                                        {r.gaps.length > 0 && (
                                            <div style={{ flex: "1 1 240px", minWidth: 220 }}>
                                                <div
                                                    style={{
                                                        fontFamily: BODY,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: BLACK,
                                                        textTransform: "uppercase",
                                                        letterSpacing: 0.5,
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    Gaps
                                                </div>
                                                <BulletList items={r.gaps} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Card>
                )}

                {/* ── Common gaps ── */}
                {synthesis && synthesis.commonGaps.length > 0 && (
                    <Card>
                        <SectionTitle>Common Gaps</SectionTitle>
                        <BulletList items={synthesis.commonGaps} />
                    </Card>
                )}

                {/* ── Placement recommendations ── */}
                {synthesis && synthesis.placementRecommendations.length > 0 && (
                    <Card>
                        <SectionTitle>In-Network Placement Recommendations</SectionTitle>
                        <div style={{ overflowX: "auto" }}>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontFamily: BODY,
                                    fontSize: 13.5
                                }}
                            >
                                <thead>
                                    <tr>
                                        {["Placement", "Channel", "Why"].map((c) => (
                                            <th
                                                key={c}
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "left",
                                                    fontWeight: 600,
                                                    color: BLACK,
                                                    fontSize: 13,
                                                    borderBottom: `2px solid ${BLACK}`,
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {c}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {synthesis.placementRecommendations.map((p, i) => (
                                        <tr key={i}>
                                            <td
                                                style={{
                                                    padding: "12px 14px",
                                                    borderBottom: `1px solid ${BORDER}`,
                                                    color: BLACK,
                                                    fontWeight: 500,
                                                    verticalAlign: "top",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {p.placement}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "12px 14px",
                                                    borderBottom: `1px solid ${BORDER}`,
                                                    color: GRAY,
                                                    verticalAlign: "top",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {p.channel}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "12px 14px",
                                                    borderBottom: `1px solid ${BORDER}`,
                                                    color: GRAY,
                                                    lineHeight: 1.55,
                                                    verticalAlign: "top"
                                                }}
                                            >
                                                {p.reason}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* ── Recommendations ── */}
                {synthesis && synthesis.recommendations.length > 0 && (
                    <Card>
                        <SectionTitle>Recommended Next Steps</SectionTitle>
                        <BulletList items={synthesis.recommendations} ordered />
                    </Card>
                )}

                {/* ── Individual audits ── */}
                {items.length > 0 && (
                    <Card style={{ padding: "28px 32px 12px" }}>
                        <SectionTitle>Individual Page Audits</SectionTitle>
                        {items.map((item, idx) => {
                            const isOpen = expanded.has(idx);
                            return (
                                <div
                                    key={`${idx}-${item.url}`}
                                    style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 12
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={item.url}
                                                style={{
                                                    fontFamily: BODY,
                                                    fontSize: 13,
                                                    color: BLACK,
                                                    display: "block",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {item.audit?.productName || item.url}
                                            </a>
                                            {item.error && (
                                                <div
                                                    style={{
                                                        fontFamily: BODY,
                                                        fontSize: 12,
                                                        color: ERROR,
                                                        marginTop: 2
                                                    }}
                                                >
                                                    Audit failed: {item.error}
                                                </div>
                                            )}
                                        </div>
                                        {typeof item.audit?.scores?.overall === "number" && (
                                            <span
                                                style={{
                                                    fontFamily: DISPLAY,
                                                    fontSize: 15,
                                                    fontWeight: 500,
                                                    color: GOLD,
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {item.audit.scores.overall}%
                                            </span>
                                        )}
                                        {item.audit && (
                                            <button
                                                type="button"
                                                onClick={() => toggle(idx)}
                                                style={{
                                                    background: isOpen ? GOLD : "none",
                                                    color: isOpen ? WHITE : GOLD,
                                                    border: `1px solid ${GOLD}`,
                                                    padding: "6px 14px",
                                                    fontFamily: DISPLAY,
                                                    fontSize: 11,
                                                    letterSpacing: 1,
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                    borderRadius: 4,
                                                    textTransform: "uppercase",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {isOpen ? "Hide audit" : "View audit"}
                                            </button>
                                        )}
                                    </div>
                                    {isOpen && item.audit && (
                                        <div
                                            style={{
                                                marginTop: 12,
                                                border: `1px solid ${BORDER}`,
                                                borderRadius: 8,
                                                overflow: "hidden"
                                            }}
                                        >
                                            <PdpAuditReport
                                                data={itemToResponse(item, reportId)}
                                                isLoggedIn
                                                proposalPath={proposalPath}
                                                proposalCtaLabel={proposalCtaLabel}
                                                productRef={toCtaProductRef(
                                                    productRefs ? productRefs[item.url] : undefined
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </Card>
                )}
            </div>
        </div>
    );
}
